import * as React from "react";
import {Fragment} from "react";
import styled from "styled-components";
import theme from "../../assets/css/theme";
import {countBy, get, isNumber, last} from "lodash";

import {
	Exist,
	IconArrowSlim,
	IconWarning,
	RegularButton,
} from "../../components";
import {IGameWeek, ISnapshot} from "../../modules/reducers";
// import ShareIcon from '../../assets/img/icons/Share.svg';
import {IUserTips} from "../../modules/types";
import {below} from "../../assets/css/media";
import ShareButton from "../../components/Buttons/ShareButton";

import IconLock from "../../assets/img/icons/lock.svg";
import IconInfo from "../../assets/img/icons/Info.svg";

// import au_ad_banner from "assets/img/ad_banners/au_ad_banner.jpg";
import br_ad_banner from "assets/img/ad_banners/br_ad_banner.jpg";
import br_ad_banner_mobile from "assets/img/ad_banners/br_ad_banner_mobile.jpg";
import ca_ad_banner from "assets/img/ad_banners/ca_ad_banner.jpg";
// import fr_ad_banner from "assets/img/ad_banners/fr_ad_banner.jpg";
// import uk_ad_banner from "assets/img/ad_banners/uk_ad_banner.jpg";
import de_ad_banner from "assets/img/ad_banners/de_ad_banner.png";
import {WeeksPlayed} from "components/Widgets/WeeksPlayed";
import {ButtonIcon} from "components/Buttons/ButtonIcon";

const SidebarWrapper = styled.div`
	background: ${theme.primary ?? "#143cab"};
`;

const DataWrapper = styled.div`
	display: flex;
	height: 80px;
	align-items: center;
	max-width: ${theme.content_width};
	margin: 0 auto;
	justify-content: space-between;
`;

const ButtonStyled = styled(RegularButton)`
	min-width: 100px;
	margin-left: 10px;
	height: 40px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	${below.desktop`
		height: 32px;
		width: auto;
		font-size: 10px;
	`};

	&:disabled {
		opacity: 0.3;
	}
`;

const WeekSwitch = styled.div`
	display: flex;
	flex: 15%;
	height: 100%;
	align-items: center;
	color: #ffffff;
	text-align: center;
	font-size: 14px;
`;

interface IArrowSlimButton {
	readonly disabled?: boolean;
}

const ArrowSlimButton = styled.div`
	border: none;
	cursor: pointer;
	opacity: ${({disabled}: IArrowSlimButton) => (disabled ? ".5" : "1")};
`;

const DisplayCurrentGameWeek = styled.div`
	font-family: ${theme.base_bold_font};
	margin: 0 10px;
	font-size: 14px;
	text-transform: uppercase;
`;

const PointsInfo = styled.div`
	display: flex;
	flex: 50%;
	justify-content: space-between;
	align-items: center;

	padding-right: 100px;

	${below.desktop`
		padding: 0 10px;
	`};
`;

const InfoItem = styled.div``;

const InfoTitle = styled.div`
	font-size: 9px;
	color: #ffffff;
	font-family: ${theme.base_font};
	text-align: center;
	letter-spacing: 1px;
	text-transform: uppercase;

	${below.desktop`
		max-width: 54px;
		height: 20px;
		display: flex;
		justify-content: center;
		flex-direction: column;
	`};
`;

const InfoValue = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 22px;
	color: #ffffff;
	font-family: ${theme.base_font_medium_display};
	text-align: center;
	margin-top: 5px;

	${below.desktop`
		font-size: 16px;
	`} svg, img {
		margin-left: 5px;
	}

	img {
		width: 10px;
		height: 12px;
	}
`;

const ButtonWrapper = styled.div`
	display: flex;
	flex: 30%;
	justify-content: center;
	align-items: center;

	button:not(.icon) {
		background-color: ${theme.home_action_button_bg ?? theme.primary};
		color: ${theme.home_action_button_color};
	}
`;

const AdsWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 30px 0;

	@media screen and (max-width: 850px) {
		a {
			width: 90%;

			img {
				width: 100%;
			}
		}
	}
`;

//
// const ShareButton = styled.div`
// 	display: block;
// 	cursor: pointer;
// 	height: 40px;
// 	width: 40px;
// 	background: url(${ShareIcon}) no-repeat center center #1B48E0;
// 	border-radius: 4px;
// `;

const MobileControlWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	background: ${theme.primary ?? "#333333"};
	height: 65px;
	padding: 0 10px;
	overflow: hidden;

	@media (max-width: 360px) {
		gap: 6px;
		padding: 0 8px;
	}
`;

const MobileWeekSwitch = styled(WeekSwitch)`
	flex: 0 0 auto;
	white-space: nowrap;

	@media (max-width: 360px) {
		${DisplayCurrentGameWeek} {
			margin: 0 6px;
			font-size: 12px;
		}
	}
`;

const MobileButtonWrapper = styled(ButtonWrapper)`
	flex: 1 1 auto;
	min-width: 0;
	justify-content: flex-end;
	gap: 8px;

	&& button {
		margin-left: 0;
	}

	@media (max-width: 360px) {
		gap: 6px;

		&& button.icon {
			min-width: 32px;
			width: 32px;
			height: 32px;
		}
	}
`;

const MobileActionButton = styled(ButtonStyled)`
	flex: 0 1 auto;
	min-width: 0;
	padding: 0 12px;
	text-transform: none;

	${below.desktop`
		height: 40px;
		font-size: 14px;
	`};

	@media (max-width: 360px) {
		height: 32px;
		padding: 0 6px;
		font-size: 11px;
	}
`;

const WIDTH_ARROW = 12;

const CIMODE = "cimode";

/**
 * Mobile has its own, shorter copy than desktop. Use the mobile key when it is
 * translated, otherwise the English default from the design.
 *
 * `fallback_key` is for labels that have room for the longer, already
 * translated desktop copy - the action buttons do not, so they stay short.
 */
const getMobileTranslation = (
	mobile_key: string,
	default_value: string,
	fallback_key?: string
) => {
	if (window.LANG_KEY === CIMODE) {
		return mobile_key;
	}

	const mobile_copy = window.getTranslations(mobile_key);

	if (mobile_copy && mobile_copy !== mobile_key) {
		return mobile_copy;
	}

	const fallback_copy = fallback_key
		? window.getTranslations(fallback_key)
		: "";

	return fallback_copy && fallback_copy !== fallback_key
		? fallback_copy
		: default_value;
};

const getTotalPointsValue = (isSeasonStarted: boolean, points: number) => {
	if (!isSeasonStarted || !isNumber(points)) {
		return "-";
	}

	return points;
};

interface IProps {
	onClickInfo: () => void;
	onSave: () => void;
	onAutopick: () => void;
	changeGameWeek: (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>
	) => void;
	readonly actual_gameweek: IGameWeek;
	readonly current_gameweek: IGameWeek;
	readonly gameweeks: IGameWeek[];
	readonly snapshot: ISnapshot;
	readonly tips: IUserTips[];
	readonly is_selected_tips: boolean;
	readonly tips_saved: boolean;
	readonly first_round: IGameWeek;
	readonly is_logged_in: boolean;
	readonly is_season_started: boolean;
}

export class Sidebar extends React.Component<IProps> {
	get week_switch() {
		const {
			changeGameWeek,
			actual_gameweek: {id: gw_id},
			gameweeks,
			current_gameweek: {id: current_gameweek_id},
			first_round,
			is_logged_in,
		} = this.props;

		const MAX_GAME_WEEK_ID = current_gameweek_id + 1;
		const last_gw_id = get(last(gameweeks), "id", MAX_GAME_WEEK_ID);
		const prev_gw = gw_id - 1;
		const prev_arrown_loced = prev_gw === first_round.id - 1;

		const next_gw = gw_id + 1;
		const next_arrown_loced =
			next_gw > MAX_GAME_WEEK_ID || last_gw_id < next_gw;

		return (
			<React.Fragment>
				<Exist when={is_logged_in}>
					<ArrowSlimButton
						data-gw_id={prev_gw}
						onClick={
							!prev_arrown_loced ? changeGameWeek : undefined
						}
						disabled={prev_arrown_loced}
					>
						<IconArrowSlim
							color="#ffffff"
							to="left"
							width={WIDTH_ARROW}
						/>
					</ArrowSlimButton>
				</Exist>

				<DisplayCurrentGameWeek>
					{window.getTranslations(
						"competitionjoin_list_placeholder_week"
					)}{" "}
					{gw_id}
				</DisplayCurrentGameWeek>
				<Exist when={is_logged_in}>
					<ArrowSlimButton
						data-gw_id={next_gw}
						onClick={
							!next_arrown_loced ? changeGameWeek : undefined
						}
						disabled={next_arrown_loced}
					>
						<IconArrowSlim
							color="#ffffff"
							to="right"
							width={WIDTH_ARROW}
						/>
					</ArrowSlimButton>
				</Exist>
			</React.Fragment>
		);
	}

	get points_info() {
		const {snapshot, actual_gameweek, is_season_started} = this.props;
		const ICON_WIDTH = 12;
		const ICON_HEIGHT = 14;

		if (!snapshot) {
			return null;
		}

		return (
			<PointsInfo>
				<InfoItem>
					<InfoTitle>
						<span className="hidden-mobile">
							{window.getTranslations(
								"pickem_gamebar_text_selected"
							)}
						</span>
						<span className="hidden-desktop">
							{getMobileTranslation(
								"pickem.gamebar.mobile.selections_made",
								"Selections made",
								"pickem_gamebar_text_selected"
							)}
						</span>
					</InfoTitle>
					<InfoValue>
						{this.selected_quantity_tips}/{this.quantity_tips}
						{this.selected_quantity_tips === this.quantity_tips ? (
							<img src={IconLock} alt="lock" />
						) : (
							<IconWarning
								color="#ffffff"
								width={ICON_WIDTH}
								height={ICON_HEIGHT}
							/>
						)}
					</InfoValue>
				</InfoItem>
				<InfoItem>
					<InfoTitle>
						{window.getTranslations(
							"competitionjoin_list_placeholder_week"
						)}{" "}
						{actual_gameweek.id}{" "}
						{window.getTranslations(
							"pickem_matchup_subheading_pts"
						)}
					</InfoTitle>
					<InfoValue>
						{getTotalPointsValue(
							is_season_started,
							this.tips_points
						)}
					</InfoValue>
				</InfoItem>
				<InfoItem>
					<InfoTitle>
						<span className="hidden-mobile">
							{window.getTranslations(
								"mycomps_list_total_points"
							)}
						</span>
						<span className="hidden-desktop">
							{getMobileTranslation(
								"pickem.gamebar.mobile.total_pts",
								"Total pts",
								"mycomps_list_total_points"
							)}
						</span>
					</InfoTitle>
					<InfoValue>
						{actual_gameweek.status === "scheduled"
							? "-"
							: snapshot.user_correct_total}
					</InfoValue>
				</InfoItem>
				<InfoItem>
					<InfoTitle>
						<span className="hidden-mobile">
							{window.getTranslations(
								"pickem_gambar_text_tiebreaker"
							)}
						</span>
						<span className="hidden-desktop">
							{getMobileTranslation(
								"pickem.gamebar.mobile.tiebreaker",
								"Tie-breaking margin",
								"pickem_gambar_text_tiebreaker"
							)}
						</span>
					</InfoTitle>
					<InfoValue>{snapshot.total_margin}</InfoValue>
				</InfoItem>
				<InfoItem>
					<InfoTitle>
						<span className="hidden-mobile">
							{window.getTranslations(
								"mycomps_list_overall_ranking"
							)}
						</span>
						<span className="hidden-desktop">
							{getMobileTranslation(
								"pickem.gamebar.mobile.overall_rank",
								"Overall rank",
								"mycomps_list_overall_ranking"
							)}
						</span>
					</InfoTitle>
					<InfoValue>{snapshot.overall_rank}</InfoValue>
				</InfoItem>
			</PointsInfo>
		);
	}

	get tips_points() {
		const {tips} = this.props;
		const points = countBy(tips, (item) => item.is_correct === 1);

		return points.true || 0;
	}

	get quantity_tips() {
		const {tips} = this.props;

		return tips.length;
	}

	get selected_quantity_tips() {
		const {tips} = this.props;
		const quantity_tips = countBy(
			tips,
			(item) => item.predict_winner_squad_id !== 0
		);

		return quantity_tips.true || 0;
	}

	get isTitpsSaved() {
		const {tips_saved, is_logged_in} = this.props;

		return tips_saved || is_logged_in;
	}

	private get isAutoPickDisabled() {
		if (!this.props.is_logged_in) {
			return true;
		}

		return this.selected_quantity_tips !== this.quantity_tips;
	}

	private get nav_button_mobile() {
		const {
			onSave,
			onAutopick,
			onClickInfo,
			tips_saved,
			is_selected_tips,
			// is_logged_in,
		} = this.props;
		const is_disabled = tips_saved || !is_selected_tips;

		const save_btn_label = tips_saved
			? getMobileTranslation(
					"pickem.gamebar.mobile.button_saved",
					"Saved"
			  )
			: getMobileTranslation("pickem.gamebar.mobile.button_save", "Save");

		const auto_btn_label =
			getMobileTranslation("pickem.gamebar.mobile.button_auto", "Auto") ||
			"";

		return (
			<React.Fragment>
				<NavShareButton isDisabled={this.isAutoPickDisabled} />
				<NavInfoButton
					onClick={onClickInfo}
					fontSize={window.LANG_KEY === "de" ? "10px" : "14px"}
				/>
				<MobileActionButton
					title={auto_btn_label}
					aria-label={auto_btn_label}
					onClick={onAutopick}
					// disabled={!is_logged_in}
				>
					{auto_btn_label}
				</MobileActionButton>
				<MobileActionButton
					aria-label={save_btn_label || ""}
					onClick={onSave}
					disabled={is_disabled}
				>
					{save_btn_label}
				</MobileActionButton>
			</React.Fragment>
		);
	}

	private get nav_button() {
		const {
			onSave,
			onAutopick,
			onClickInfo,
			tips_saved,
			is_selected_tips,
			// is_logged_in,
		} = this.props;
		const is_disabled = this.isTitpsSaved || !is_selected_tips;
		return (
			<React.Fragment>
				<NavShareButton isDisabled={this.isAutoPickDisabled} />
				<NavInfoButton
					onClick={onClickInfo}
					fontSize={window.LANG_KEY === "de" ? "10px" : "14px"}
				/>
				<NavAutoPick
					fontSize={window.LANG_KEY === "de" ? "10px" : "14px"}
					label={
						window.getTranslations(
							"pickem_gamebar_button_autpick"
						) || ""
					}
					onClick={onAutopick}
					// disabled={!is_logged_in}
				/>
				<NavSavePick
					isDisabled={is_disabled}
					label={
						tips_saved
							? window.getTranslations(
									"pickem_gamebar_button_saved"
							  ) || ""
							: window.getTranslations(
									"pickem_gamebar_button_save"
							  ) || ""
					}
					onClick={onSave}
				/>
			</React.Fragment>
		);
	}

	private get mobile_control() {
		return (
			<MobileControlWrapper className="hidden-desktop">
				<MobileWeekSwitch>{this.week_switch}</MobileWeekSwitch>
				<MobileButtonWrapper>
					{this.nav_button_mobile}
				</MobileButtonWrapper>
			</MobileControlWrapper>
		);
	}

	private get adBanners() {
		const current_lang = process.env.REACT_APP_AD_KEY || "";

		const adBlock =
			{
				de: (
					<Fragment>
						<a
							href="https://ad.doubleclick.net/ddm/trackclk/N8807.5769257BWINLANDINGPAGE/B34033134.427793106;dc_trk_aid=620423082;dc_trk_cid=240559701;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;ltd=;dc_tdv=1"
							target="_blank"
							rel="noopener noreferrer"
						>
							<img src={de_ad_banner} alt="Ad" />
							<img
								src="https://ad.doubleclick.net/ddm/trackimp/N8807.5769257BWINLANDINGPAGE/B34033134.427793106;dc_trk_aid=620423082;dc_trk_cid=240559701;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};npa=1;ltd=;dc_tdv=1?"
								alt="Advertisement"
								style={{
									border: "0px",
									height: "1px",
									maxHeight: "1px",
									width: "1px",
									opacity: 0,
								}}
							/>
						</a>
					</Fragment>
				),
				br: (
					<Fragment>
						<a
							href="https://ad.doubleclick.net/ddm/trackclk/N927339.4132995NFLNETWORK/B32326361.402601579;dc_trk_aid=594685866;dc_trk_cid=219534647;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;ltd=;dc_tdv=1"
							target="_blank"
							rel="noopener noreferrer"
							className="hidden-mobile"
						>
							<img src={br_ad_banner} alt="Ad" />
						</a>
						<a
							href="https://ad.doubleclick.net/ddm/trackclk/N927339.4132995NFLNETWORK/B32326361.427192844;dc_trk_aid=620139750;dc_trk_cid=219534647;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;ltd=;dc_tdv=1"
							target="_blank"
							rel="noopener noreferrer"
							className="hidden-desktop"
						>
							<img src={br_ad_banner_mobile} alt="Ad" />
						</a>
					</Fragment>
				),
				ca: (
					<Fragment>
						<a
							href="https://www.nflshop.ca/en/"
							target="_blank"
							rel="noopener noreferrer"
						>
							<img src={ca_ad_banner} alt="Ad" />
						</a>
					</Fragment>
				),
			}[current_lang] || null;

		return adBlock ? <AdsWrapper>{adBlock}</AdsWrapper> : null;
	}

	/**
	 * @ignore
	 */
	public render() {
		// const {actual_gameweek, current_gameweek} = this.props;

		return (
			<Fragment>
				<SidebarWrapper>
					<DataWrapper>
						<WeekSwitch className="hidden-mobile">
							{this.week_switch}
						</WeekSwitch>
						{this.points_info}
						<ButtonWrapper className="hidden-mobile">
							{this.nav_button}
						</ButtonWrapper>
					</DataWrapper>
					{this.mobile_control}
				</SidebarWrapper>
				<WeeksPlayed />
				{this.adBanners}
			</Fragment>
		);
	}
}

function NavShareButton({isDisabled}: {isDisabled: boolean}) {
	return (
		<ShareButton
			aria-label="share"
			gamebar={true}
			disabled={isDisabled}
			section="general"
		/>
	);
}

function NavInfoButton({
	fontSize,
	onClick,
}: {
	fontSize: string;
	onClick: () => void;
}) {
	return (
		<ButtonIcon
			className="icon"
			aria-label="information"
			onClick={onClick}
			font_size={fontSize}
		>
			<img alt="information" src={IconInfo} />
		</ButtonIcon>
	);
}

function NavAutoPick({
	fontSize,
	label,
	onClick,
	disabled,
}: {
	fontSize: string;
	label: string;
	onClick: () => void;
	disabled?: boolean;
}) {
	return (
		<ButtonStyled
			title={label}
			aria-label={label}
			onClick={onClick}
			font_size={fontSize}
			disabled={disabled}
		>
			{label}
		</ButtonStyled>
	);
}

function NavSavePick({
	isDisabled,
	label,
	onClick,
}: {
	isDisabled: boolean;
	label: string;
	onClick: () => void;
}) {
	return (
		<ButtonStyled
			aria-label={label}
			onClick={onClick}
			disabled={isDisabled}
		>
			{label}
		</ButtonStyled>
	);
}

export default Sidebar;
